import { IReactionDisposer, action, makeObservable, override, reaction, runInAction } from 'mobx';

import { TreeNodeFolder } from '@/entities/Tree';
import { notificationsStore } from '@/stores/global/RootStore';
import { DocumentsTreeSelectionStore } from '@/stores/local/shared/DocumentsTreeSelectionStore/DocumentsTreeSelectionStore';
import { PopupStore } from '@/stores/local/shared/PopupStore';
import { TreeStore } from '@/stores/local/shared/TreeStore';
import { type DocumentSourceClassName, detectDocumentSourceClass } from '@/utils/detectDocumentSourceClass';
import { getFolderAncestorNames } from '@/utils/getFolderAncestorNames';

import { UploadDocumentStore } from '../../../UploadDocumentStore';

import { DocumentSourceTypeSelectStore } from './DocumentSourceTypeSelectStore';

type UploadDocumentPopupStoreParams = {
  getOrganizationId: () => number;
  getOrganizationName: () => string | null;
  treeStore: TreeStore;
  selectionStore: DocumentsTreeSelectionStore;
};

type PrefillSnapshot = {
  fileNamesKey: string;
  filesCount: number;
  folderId: string | null;
  organizationName: string | null;
  optionsLoaded: boolean;
};

export class UploadDocumentPopupStore extends PopupStore<TreeNodeFolder | null> {
  readonly uploadDocumentStore: UploadDocumentStore;
  readonly documentSourceTypeSelectStore: DocumentSourceTypeSelectStore;

  private readonly _treeStore: TreeStore;
  private readonly _selectionStore: DocumentsTreeSelectionStore;
  private readonly _getOrganizationName: () => string | null;

  private _isSourceManuallySet = false;
  private _prefillDisposer?: IReactionDisposer;

  constructor({ getOrganizationId, getOrganizationName, treeStore, selectionStore }: UploadDocumentPopupStoreParams) {
    super();

    this._treeStore = treeStore;
    this._selectionStore = selectionStore;
    this._getOrganizationName = getOrganizationName;

    this.uploadDocumentStore = new UploadDocumentStore({
      getOrganizationId,
      treeStore,
    });
    this.documentSourceTypeSelectStore = new DocumentSourceTypeSelectStore({
      getOrganizationId,
      onManualSelect: () => {
        this._isSourceManuallySet = true;
      },
    });

    makeObservable(this, {
      addDocument: action.bound,
      open: override,
      openWithNull: override,
      close: override,
    });
  }

  open(entity: TreeNodeFolder | null = null): void {
    this.entity = entity;
    super.open();
    void this._onPopupOpen();
  }

  openWithNull(): void {
    this._selectionStore.clear();
    this._selectionStore.setSelectAll(false);
    this.open(null);
  }

  close(): void {
    this._stopPrefillSync();
    super.close();
    this._resetSourceSelect();
  }

  changeOrganizationId(): void {
    this.close();
    this.uploadDocumentStore.reset();
    this.uploadDocumentStore.meta.reset();
    this.documentSourceTypeSelectStore.resetOptions();
    this.documentSourceTypeSelectStore.meta.reset();
  }

  async addDocument(id?: string): Promise<void> {
    const folderId = id ?? this.entity?.id;
    const sourceClassId = this.documentSourceTypeSelectStore.serverFormat;

    await this.uploadDocumentStore.addDocuments(folderId, sourceClassId);

    runInAction(() => {
      if (this.uploadDocumentStore.meta.isLoaded && this.uploadDocumentStore.failedFiles.length === 0) {
        this.uploadDocumentStore.reset();
        this._resetSourceSelect();
        this.close();
      }
    });
  }

  destroy(): void {
    this._stopPrefillSync();
    super.destroy();
    this.uploadDocumentStore.destroy();
    this.documentSourceTypeSelectStore.destroy();
    this.documentSourceTypeSelectStore.meta.destroy();
  }

  private _resetSourceSelect(): void {
    this._isSourceManuallySet = false;
    this.documentSourceTypeSelectStore.resetValue();
    this.documentSourceTypeSelectStore.unTouch();
    this.documentSourceTypeSelectStore.resetOptions();
  }

  private async _onPopupOpen(): Promise<void> {
    this._resetSourceSelect();
    this._setupPrefillSync();
    await this.documentSourceTypeSelectStore.load(true);
  }

  private _getTargetFolderId(): string | undefined {
    return this.entity?.id ?? this._selectionStore.singleFolderToServerFormat;
  }

  private _getPrefillSnapshot(): PrefillSnapshot {
    const files = this.uploadDocumentStore.files;

    return {
      fileNamesKey: files.map((file) => file.name).join('\n'),
      filesCount: files.length,
      folderId: this._getTargetFolderId() ?? null,
      organizationName: this._getOrganizationName(),
      optionsLoaded: this.documentSourceTypeSelectStore.meta.isLoaded,
    };
  }

  private _setupPrefillSync(): void {
    this._stopPrefillSync();

    this._prefillDisposer = reaction(
      () => this._getPrefillSnapshot(),
      (snapshot, previousSnapshot) => {
        if (!snapshot.optionsLoaded) {
          return;
        }

        if (snapshot.filesCount === 0) {
          const filesWereCleared = previousSnapshot !== undefined && previousSnapshot.filesCount > 0;

          if (filesWereCleared) {
            this._isSourceManuallySet = false;
            this.documentSourceTypeSelectStore.resetValue();
            this.documentSourceTypeSelectStore.unTouch();
          }

          return;
        }

        const filesChanged =
          previousSnapshot !== undefined &&
          (previousSnapshot.fileNamesKey !== snapshot.fileNamesKey ||
            previousSnapshot.filesCount !== snapshot.filesCount);
        const folderChanged = previousSnapshot !== undefined && previousSnapshot.folderId !== snapshot.folderId;
        const organizationNameChanged =
          previousSnapshot !== undefined && previousSnapshot.organizationName !== snapshot.organizationName;
        const optionsJustLoaded =
          previousSnapshot !== undefined && !previousSnapshot.optionsLoaded && snapshot.optionsLoaded;

        const shouldRecalculate =
          !this._isSourceManuallySet && (optionsJustLoaded || folderChanged || filesChanged || organizationNameChanged);

        if (shouldRecalculate) {
          this._recalculateSourceClassPrefill();
        }
      },
      {
        fireImmediately: true,
      },
    );
  }

  private _stopPrefillSync(): void {
    if (this._prefillDisposer) {
      this._prefillDisposer();
      this._prefillDisposer = undefined;
    }
  }

  private _recalculateSourceClassPrefill(): void {
    const { files } = this.uploadDocumentStore;

    if (files.length === 0 || !this.documentSourceTypeSelectStore.meta.isLoaded) {
      return;
    }

    const folderNames = getFolderAncestorNames(this._treeStore, this._getTargetFolderId());
    const detectedClass = detectDocumentSourceClass({
      fileNames: files.map((file) => file.name),
      folderNames,
      organizationName: this._getOrganizationName(),
    });

    this._applySourceClassOption(detectedClass);
  }

  private _applySourceClassOption(className: DocumentSourceClassName): void {
    const option = this.documentSourceTypeSelectStore.findOptionByName(className);

    if (!option) {
      notificationsStore.showWarning('Не удалось автоматически определить класс источника. Выберите значение вручную.');

      return;
    }

    this.documentSourceTypeSelectStore.setValue([option], { noTouch: true });
    this.documentSourceTypeSelectStore.unTouch();
  }
}
